import moment from 'moment';
import {
    IconBellRinging,
    IconBible,
    IconBook2,
    IconBread,
    IconBuildingChurch,
    IconCandle,
    IconCross,
    IconDroplet,
    IconFlame,
    IconFlower,
    IconHeart,
    IconMapPin,
    IconPray,
    IconRosette,
    IconSun,
    IconUsersGroup,
} from '@tabler/icons-react';
import { defineMessages, useIntl } from 'react-intl';

import styles from './UpcomingEventsPanel.module.scss';

import { Event } from 'Components/Common/models/Event';

type Props = {
    events: Event[];
    isFetching: boolean;
    onEventSelect: (event: Event) => void;
    onLoadMore: () => void;
};

const upcomingEventIcons = [
    IconBuildingChurch,
    IconCross,
    IconBible,
    IconCandle,
    IconBread,
    IconBellRinging,
    IconPray,
    IconDroplet,
    IconHeart,
    IconBook2,
    IconSun,
    IconRosette,
    IconFlower,
    IconFlame,
    IconUsersGroup,
];

const hashString = (value: string) => Array.from(value).reduce((hash, char) => (
    (hash * 31) + char.charCodeAt(0)
), 0);

const getUpcomingEventIcon = (value: string) => {
    const index = Math.abs(hashString(value)) % upcomingEventIcons.length;
    return upcomingEventIcons[index];
};

const UpcomingEventsPanel = ({
    events,
    isFetching,
    onEventSelect,
    onLoadMore,
}: Props) => {
    const intl = useIntl();

    return (
        <aside className={styles.upcomingPanel}>
            <h2>{intl.formatMessage(messages.heading)}</h2>
            <div className={styles.upcomingList}>
                {events.map((event) => {
                    const Icon = getUpcomingEventIcon(`${event.title}-${event.categoryIds.join('-')}`);
                    const eventStart = moment(event.dateFrom);

                    return (
                        <button
                            key={event.occurenceId}
                            type="button"
                            className={styles.upcomingEvent}
                            onClick={() => { onEventSelect(event); }}
                        >
                            <span className={styles.upcomingIcon}>
                                <Icon size={20} stroke={2} />
                            </span>
                            <span className={styles.upcomingDetails}>
                                <span className={styles.upcomingMeta}>
                                    <span>{eventStart.format('dddd').toUpperCase()}</span>
                                    <span>
                                        {event.allDay
                                            ? intl.formatMessage(messages.allDay)
                                            : eventStart.format('HH:mm')}
                                    </span>
                                </span>
                                <span className={styles.upcomingDate}>
                                    {eventStart.format('D MMMM')}
                                </span>
                                <span className={styles.upcomingTitle}>{event.title}</span>
                                {event.eventPlace && (
                                    <span className={styles.upcomingPlace}>
                                        <IconMapPin size={14} stroke={2} />
                                        {event.eventPlace}
                                    </span>
                                )}
                            </span>
                        </button>
                    );
                })}
                {isFetching && events.length > 0 && (
                    <div className={styles.upcomingLoading}>{intl.formatMessage(messages.loading)}</div>
                )}
                {!isFetching && events.length === 0 && (
                    <div className={styles.upcomingEmpty}>{intl.formatMessage(messages.empty)}</div>
                )}
            </div>
            <button
                type="button"
                className={styles.loadMoreEventsButton}
                onClick={onLoadMore}
                disabled={isFetching}
            >
                {intl.formatMessage(messages.loadMore)}
            </button>
        </aside>
    );
};

const messages = defineMessages({
    heading: {
        id: 'src.Screens.Dashboard.Events.UpcomingEventsPanel.heading',
        defaultMessage: 'Najbliższe wydarzenia',
    },
    loading: {
        id: 'src.Screens.Dashboard.Events.UpcomingEventsPanel.loading',
        defaultMessage: 'Ładowanie...',
    },
    empty: {
        id: 'src.Screens.Dashboard.Events.UpcomingEventsPanel.empty',
        defaultMessage: 'Brak nadchodzących wydarzeń',
    },
    loadMore: {
        id: 'src.Screens.Dashboard.Events.UpcomingEventsPanel.loadMore',
        defaultMessage: 'Pobierz kolejne wydarzenia',
    },
    allDay: {
        id: 'src.Screens.Dashboard.Events.UpcomingEventsPanel.allDay',
        defaultMessage: 'Całodniowe',
    },
});

export default UpcomingEventsPanel;
